import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle } from '@untitledui/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { RadioButton, RadioGroup } from '@/lib/base/radio-buttons';
import { Wizard, type WizardStep } from '@/lib/base/wizard';

import { startStripeBackfill } from '../api/connect';
import type { StripeBackfillDepth } from '../types';

interface StripeBackfillWizardProps {
  orgId: number | string;
  connectionId: string | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onFinished?: () => void;
}

export function StripeBackfillWizard({
  orgId,
  connectionId,
  isOpen,
  onOpenChange,
  onFinished,
}: StripeBackfillWizardProps) {
  const queryClient = useQueryClient();
  const [depth, setDepth] = useState<StripeBackfillDepth>('all_time');
  const [finishError, setFinishError] = useState<string | null>(null);

  // Reset wizard-local state each time it opens so a reopen starts at the
  // default depth with no stale inline error from a prior session.
  useEffect(() => {
    if (isOpen) {
      setDepth('all_time');
      setFinishError(null);
    }
  }, [isOpen]);

  const handleDepthChange = useCallback((value: string) => {
    if (value === '90_days' || value === '1_year' || value === 'all_time') {
      setDepth(value);
    }
  }, []);

  // Fire onFinished as the user leaves the Done confirmation step (the CTA below), not on
  // start — invoking it in handleStart closes the wizard before ctx.next() can advance, so
  // the user never sees the Done step.
  const handleClose = useCallback(() => {
    onFinished?.();
    onOpenChange(false);
  }, [onFinished, onOpenChange]);

  const handleStart = useCallback(async () => {
    setFinishError(null);
    if (!connectionId) {
      setFinishError(
        "We couldn't find the connected account. Please reconnect and try again."
      );
      throw new Error('no connection');
    }
    try {
      await startStripeBackfill(orgId, connectionId, depth);
      await queryClient.invalidateQueries({
        queryKey: ['stripeConnections', orgId],
      });
    } catch (err) {
      setFinishError("We couldn't start the import. Please try again.");
      throw err;
    }
  }, [connectionId, orgId, depth, queryClient]);

  const steps = useMemo<WizardStep[]>(
    () => [
      {
        id: 'backfill',
        label: 'History',
        title: 'How much history should we import?',
        subtitle: 'Choose how far back to import your Stripe data.',
        render: () => (
          <div className='flex flex-col gap-3'>
            <RadioGroup
              aria-label='Backfill depth'
              value={depth}
              onChange={handleDepthChange}
              className='gap-3'
            >
              <RadioButton value='90_days' label='Last 90 days' />
              <RadioButton value='1_year' label='Last year' />
              <RadioButton value='all_time' label='All time' />
            </RadioGroup>
            {finishError && (
              <p
                role='alert'
                className='text-body-small text-[var(--color-text-error-primary)]'
              >
                {finishError}
              </p>
            )}
          </div>
        ),
        primaryCta: {
          label: 'Start import',
          onClick: async ctx => {
            await handleStart();
            ctx.next();
          },
        },
      },
      {
        id: 'done',
        label: 'Done',
        hideFooter: true,
        render: () => (
          <div className='flex flex-col items-center gap-4 py-6 text-center'>
            <FeaturedIcon
              icon={CheckCircle}
              color='success'
              theme='light-outline'
              size='lg'
            />
            <div>
              <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
                You&apos;re all set
              </h3>
              <p className='mt-1 max-w-md text-body-small text-[var(--color-text-secondary)]'>
                We&apos;re importing your Stripe data in the background.
                It&apos;ll appear in Ahoy as it syncs.
              </p>
            </div>
            <Button color='primary' size='md' onClick={handleClose}>
              Go to Stripe settings
            </Button>
          </div>
        ),
      },
    ],
    [depth, handleDepthChange, finishError, handleStart, handleClose]
  );

  return (
    <Wizard
      mode='fullscreen'
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      steps={steps}
      defaultStepId='backfill'
      dialogAriaLabel='Set up Stripe'
      instance='stripe-backfill-wizard'
      discardConfirmation={false}
    />
  );
}
